import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShare } from '../context/ShareContext';
import { useCommunity } from '../context/CommunityContext';
import AdBlock from '../components/AdBlock';
import FavoriteButton from '../components/FavoriteButton';
import MenuItemLikeButton from '../components/MenuItemLikeButton';
import ShareButton from '../components/ShareButton';
import ReportDialog from '../components/ReportDialog';
import ReservationPanel from '../components/ReservationPanel';
import Seo from '../components/Seo';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { engagementService, menuEngagementKey } from '../services/engagementService';
import { isCommercialMenuCatalog, normalizeCommercialMenu } from '../lib/commercialMenu';
import { toDistance } from '../lib/distance';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value.split(',').map((entry) => entry.trim()).filter(Boolean);
  }
};

const textOf = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return value.label || value.name || value.title || value.id || '';
  return String(value);
};

const dateOf = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('de-DE');
};

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    listings = [],
    dbCommercialOffers = [],
    commercialOffers = [],
    providers = [],
    user,
    startChat,
    calculateDistance,
  } = useShare();
  const { blockedIds } = useCommunity();
  const [notFoundReady, setNotFoundReady] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [menuEngagement, setMenuEngagement] = useState({});

  const offers = dbCommercialOffers.length ? dbCommercialOffers : commercialOffers;

  const listing = useMemo(() => {
    const privateListing = listings.find((item) => String(item.id) === String(id));
    if (privateListing) {
      return {
        ...privateListing,
        providerId: privateListing.providerId || privateListing.provider_id,
        providerName: privateListing.isAnonymous || privateListing.is_anonymous ? 'Anonym' : (privateListing.providerName || privateListing.provider_name),
        imageUrl: privateListing.imageUrl || privateListing.image_url,
        expDate: privateListing.expDate || privateListing.exp_date,
        cookedAt: privateListing.cookedAt || privateListing.cooked_at,
        categories: asArray(privateListing.categories),
        allergens: asArray(privateListing.allergens),
        weeklyMenu: [],
        isCommercial: false,
      };
    }

    const commercial = offers.find((item) => {
      const offerId = String(item.id);
      const routeId = String(id);
      return routeId === offerId || routeId === `menu-${offerId}` || routeId.startsWith(`meal-${offerId}-`);
    });
    if (!commercial) return null;

    const providerId = commercial.providerId || commercial.provider_id;
    const provider = providers.find((item) => String(item.id) === String(providerId));
    return {
      ...commercial,
      providerId,
      providerName: commercial.providerName || commercial.provider_name || provider?.name,
      imageUrl: commercial.imageUrl || commercial.image_url,
      logoUrl: commercial.logo_url || provider?.logo_url,
      expDate: commercial.expDate || commercial.exp_date || commercial.valid_until,
      categories: asArray(commercial.categories),
      allergens: asArray(commercial.allergens),
      weeklyMenu: asArray(commercial.weekly_menu),
      isCommercial: true,
    };
  }, [id, listings, offers, providers]);

  const listingId = listing?.id;
  const listingIsCommercial = Boolean(listing?.isCommercial);

  useEffect(() => {
    if (listing) {
      setNotFoundReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setNotFoundReady(true), 1800);
    return () => window.clearTimeout(timer);
  }, [listing, id]);

  useEffect(() => {
    if (!listingId) return undefined;
    let active = true;

    if (listingIsCommercial) {
      engagementService.getMenuItemEngagement([listingId])
        .then((result) => {
          if (active) setMenuEngagement(result);
        })
        .catch((error) => console.warn('Gericht-Likes konnten nicht geladen werden:', error));
    } else {
      engagementService.recordListingView(listingId)
        .then((result) => {
          if (active) setViewCount(result.viewCount);
        })
        .catch(async (error) => {
          console.warn('Angebotsaufruf konnte nicht gezählt werden:', error);
          try {
            const counts = await engagementService.getListingViewCounts([listingId]);
            if (active) setViewCount(Number(counts[String(listingId)] || 0));
          } catch (countError) {
            console.warn('Aufrufzahl konnte nicht geladen werden:', countError);
          }
        });
    }

    return () => {
      active = false;
    };
  }, [listingId, listingIsCommercial]);

  const handleMenuEngagementChange = useCallback((result) => {
    setMenuEngagement((current) => ({
      ...current,
      [menuEngagementKey(result.offerId, result.itemIndex)]: result,
    }));
  }, []);

  const distance = useMemo(() => {
    if (!listing || typeof calculateDistance !== 'function') return null;
    const value = toDistance(calculateDistance(listing));
    return Number.isFinite(value) ? value : null;
  }, [listing, calculateDistance]);

  const startConversation = useCallback(async () => {
    if (!user) return navigate('/auth');
    if (String(listing.providerId) === String(user.id)) return alert('Du kannst keine Nachricht an dich selbst senden');
    try {
      const chatId = await startChat(listing);
      if (chatId) navigate(`/chat/${chatId}`);
    } catch (error) {
      console.error('Chat konnte nicht gestartet werden:', error);
      alert('Die Anfrage konnte nicht gestartet werden.');
    }
  }, [user, listing, startChat, navigate]);

  if (!listing && !notFoundReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Angebot wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl border p-7 text-center max-w-md">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-4xl text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Angebot nicht verfügbar</h1>
          <p className="text-gray-600 mb-5">Das Angebot wurde nicht gefunden oder ist nicht mehr verfügbar.</p>
          <button type="button" onClick={() => navigate('/feed')} className="bg-green-600 text-white px-5 py-3 rounded-xl">Zur Übersicht</button>
        </div>
      </div>
    );
  }

  const targetType = listing.isCommercial ? 'commercial_offer' : 'listing';
  const isBlocked = listing.providerId && blockedIds.has(String(listing.providerId));
  const allergens = asArray(listing.allergens).map(textOf).filter(Boolean);
  const categories = asArray(listing.categories).map(textOf).filter(Boolean);
  const menu = listing.isCommercial
    ? normalizeCommercialMenu(listing.weeklyMenu || listing.weekly_menu, new Date())
    : [];
  const menuIsCatalog = isCommercialMenuCatalog(listing);
  const hasCurrentDay = menu.some((item) => item.isToday);
  const pageUrl = `https://schleishare.de/listing/${listing.id}`;
  const description = String(listing.description || (listing.isCommercial ? `Mittagstisch von ${listing.providerName || 'einem regionalen Anbieter'}` : 'Lebensmittelangebot aus der Schlei-Region')).slice(0, 180);
  const structuredData = listing.isCommercial ? {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: listing.title,
    description,
    provider: { '@type': 'FoodEstablishment', name: listing.providerName || 'Anbieter', address: listing.address || undefined },
    hasMenuSection: menu.length ? [{ '@type': 'MenuSection', name: menuIsCatalog ? 'Speisekarte' : 'Wochenkarte', hasMenuItem: menu.map((item) => ({ '@type': 'MenuItem', name: item.meal, offers: item.price ? { '@type': 'Offer', price: item.price } : undefined })) }] : undefined,
  } : {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    description,
    image: listing.imageUrl || undefined,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', availability: 'https://schema.org/InStock' },
  };

  if (isBlocked && String(listing.providerId) !== String(user?.id)) {
    return (
      <div className="min-h-[65vh] flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <SafeIcon icon={FiIcons.FiUserX} className="mx-auto mb-4 text-5xl text-gray-300" />
          <h1 className="text-xl font-bold text-gray-900">Inhalt eines blockierten Nutzers</h1>
          <p className="mt-2 text-gray-500">Du kannst die Blockierung im Profil des Nutzers aufheben.</p>
          <button type="button" onClick={() => navigate('/feed')} className="mt-6 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white">Zur Übersicht</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo title={listing.title} description={description} image={listing.imageUrl} url={pageUrl} type={listing.isCommercial ? 'restaurant.menu' : 'product'} structuredData={structuredData} />
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-green-700">
            <SafeIcon icon={FiIcons.FiChevronLeft} /> Zurück
          </button>
          <div className="flex items-center gap-2">
            <FavoriteButton targetType={targetType} targetId={listing.id} label={false} />
            <ShareButton title={listing.title} text={description} url={pageUrl} className="px-3" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <AdBlock slot="listing-top" />
        <article className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {listing.imageUrl ? (
            <img src={listing.imageUrl} alt={listing.title || 'Angebot'} className="w-full h-64 object-cover" />
          ) : listing.logoUrl ? (
            <div className="h-64 bg-white border-b border-gray-100 flex items-center justify-center p-8">
              <img src={listing.logoUrl} alt={listing.providerName || 'Anbieter'} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="h-64 bg-gray-100 flex items-center justify-center"><SafeIcon icon={FiIcons.FiImage} className="text-5xl text-gray-300" /></div>
          )}

          <div className="p-6">
            {listing.isCommercial && <span className="inline-block text-xs font-bold bg-green-100 text-green-800 px-3 py-1 rounded-full mb-3">Mittagstisch</span>}
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-gray-600">
                  <p className="flex items-center gap-2"><SafeIcon icon={listing.isCommercial ? FiIcons.FiBriefcase : FiIcons.FiUser} />{listing.providerName || 'Anbieter'}</p>
                  {!listing.isCommercial && <p className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiEye} />{viewCount} {viewCount === 1 ? 'Aufruf' : 'Aufrufe'}</p>}
                </div>
              </div>
              {distance !== null && <span className="bg-green-50 text-green-800 px-3 py-1 rounded-full text-sm h-fit">{distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`} entfernt</span>}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <section>
                <h2 className="font-semibold mb-2">Beschreibung</h2>
                <p className="text-gray-600 whitespace-pre-line">{listing.description || 'Keine Beschreibung verfügbar'}</p>
              </section>
              <section>
                <h2 className="font-semibold mb-2">Details</h2>
                <div className="space-y-2 text-gray-600">
                  {dateOf(listing.cookedAt) && <p><SafeIcon icon={FiIcons.FiCalendar} className="inline mr-2" />Zubereitet am {dateOf(listing.cookedAt)}</p>}
                  {dateOf(listing.expDate) && <p><SafeIcon icon={FiIcons.FiClock} className="inline mr-2" />{listing.isCommercial ? 'Gültig' : 'Abholung'} bis {dateOf(listing.expDate)}</p>}
                  {!listing.isCommercial && listing.quantity && <p><SafeIcon icon={FiIcons.FiPackage} className="inline mr-2" />{listing.quantity} Portionen</p>}
                  {listing.price !== undefined && listing.price !== null && listing.price !== '' && <p><SafeIcon icon={FiIcons.FiTag} className="inline mr-2" />{listing.price}</p>}
                </div>
              </section>
            </div>

            {listing.isCommercial && listing.menu_pdf_url && (
              <a href={listing.menu_pdf_url} target="_blank" rel="noreferrer" className="mb-6 inline-flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 hover:bg-red-100">
                <SafeIcon icon={FiIcons.FiFileText} /> Speiseplan als PDF öffnen
              </a>
            )}

            {menu.length > 0 && (
              <section className="border-t pt-6 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="font-semibold">{menuIsCatalog ? 'Speisekarte' : 'Wochenkarte'}</h2>
                  {hasCurrentDay && <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">Heutiger Tag hervorgehoben</span>}
                </div>
                <div className="space-y-3">
                  {menu.map((item, index) => {
                    const engagement = menuEngagement[menuEngagementKey(listing.id, item.originalIndex)] || {};
                    return (
                      <div key={`${item.meal || 'Gericht'}-${item.dateLabel || item.dayLabel}-${index}`} aria-current={item.isToday ? 'date' : undefined} className={`rounded-xl px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border ${item.isToday ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200' : 'bg-gray-50 border-gray-100'}`}>
                        <div className="min-w-0">
                          {(item.dayLabel || item.dateLabel) && (
                            <div className="flex flex-wrap items-center gap-2 mb-1 text-sm">
                              {item.dayLabel && <strong className="text-gray-900">{item.dayLabel}</strong>}
                              {item.dateLabel && <span className="text-gray-500">{item.dateLabel}</span>}
                              {item.isToday && <span className="text-[10px] font-black uppercase tracking-wide text-white bg-emerald-600 px-2 py-0.5 rounded-full">Heute</span>}
                            </div>
                          )}
                          <p className="font-medium text-gray-900">{item.meal || 'Gericht'}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {item.price && <strong className="text-lg text-gray-900">{item.price}</strong>}
                          <MenuItemLikeButton
                            offerId={listing.id}
                            itemIndex={item.originalIndex}
                            count={engagement.likeCount || 0}
                            liked={engagement.likedByMe || false}
                            onChange={handleMenuEngagementChange}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {(allergens.length > 0 || listing.ingredients) && (
              <section className="border-t pt-6 mb-6">
                <h2 className="font-semibold mb-3">Zutaten & Allergene</h2>
                {listing.ingredients && <p className="text-gray-600 mb-3">{listing.ingredients}</p>}
                <div className="flex flex-wrap gap-2">{allergens.map((item, index) => <span key={`${item}-${index}`} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">{item}</span>)}</div>
              </section>
            )}

            {categories.length > 0 && <div className="border-t pt-6 mb-6 flex flex-wrap gap-2">{categories.map((item, index) => <span key={`${item}-${index}`} className="text-xs bg-gray-100 px-2 py-1 rounded">{item}</span>)}</div>}

            {!listing.isCommercial && <ReservationPanel listing={listing} />}

            <div className="border-t pt-6 flex flex-col gap-5">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <p className="text-gray-600"><SafeIcon icon={FiIcons.FiMapPin} className="inline mr-2" />{listing.isCommercial ? (listing.address || 'Adresse nicht angegeben') : 'Abholadresse wird erst im Chat geteilt'}</p>
                {listing.isCommercial ? (
                  listing.providerId && <button type="button" onClick={() => navigate(`/provider/${listing.providerId}`)} className="bg-green-600 text-white px-5 py-3 rounded-xl">Zum Anbieter</button>
                ) : (
                  <button type="button" onClick={startConversation} className="bg-green-600 text-white px-5 py-3 rounded-xl">Anfragen</button>
                )}
              </div>
              {String(listing.providerId) !== String(user?.id) && <ReportDialog targetType={targetType} targetId={listing.id} targetUserId={listing.isCommercial ? null : listing.providerId} />}
            </div>
          </div>
        </article>
        <AdBlock slot="listing-bottom" />
      </div>
    </div>
  );
};

export default ListingDetail;
